import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { COMPANY_STORAGE_KEY, readStoredCompany } from "../utils/company";

const ACCENT_PRESETS = ["#d97706", "#007AFF", "#10b981", "#ec4899", "#8b5cf6"];

export const ACCENT_OPTIONS = [
  { value: "#d97706", label: "Amber" },
  { value: "#007AFF", label: "Blue" },
  { value: "#10b981", label: "Green" },
  { value: "#ec4899", label: "Pink" },
  { value: "#8b5cf6", label: "Violet" },
];

const StateContext = createContext({
  user: null,
  token: null,
  roles: [],
  permissions: [],
  unseenReleases: [],
  company: null,
  accentColor: ACCENT_PRESETS[0],
  setUser: () => { },
  setToken: () => { },
  setRoles: () => { },
  setPermissions: () => { },
  setMessage: () => { },
  setUnseenReleases: () => { },
  setCompany: () => { },
  setAccentColor: () => { },
})

// The last company profile we saw, so the shell and any invoice rendered in the
// first moments after a reload have a name and address to print instead of a
// blank letterhead. Refreshed from the API on every mount.

export const ContextProvider = ({ children }) => {

  const [user, setUser] = useState({});
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [unseenReleases, setUnseenReleases] = useState([]);
  const [token, _setToken] = useState(localStorage.getItem("ACCESS_TOKEN"));
  const [message, setMessage] = useState("");
  // Default to Dark Mode if no preference is stored
  const [darkMode, _setDarkMode] = useState(localStorage.getItem("DARK_MODE") === null ? true : localStorage.getItem("DARK_MODE") === 'true');
  const [accentColor, _setAccentColor] = useState(localStorage.getItem("ACCENT_COLOR") || ACCENT_PRESETS[0]);
  const [company, _setCompany] = useState(readStoredCompany);

  // The browser tab. Set from the profile wherever it is known — the static
  // title in index.html is only what shows before the app has said otherwise.
  useEffect(() => {
    if (company?.name) document.title = company.name;
  }, [company]);

  const setCompany = useCallback((profile) => {
    _setCompany(profile);
    try {
      if (profile) localStorage.setItem(COMPANY_STORAGE_KEY, JSON.stringify(profile));
      else localStorage.removeItem(COMPANY_STORAGE_KEY);
    } catch {
      // Out of quota or a private-mode window: the profile still lives in
      // state for this session, it just will not survive the next reload.
    }
  }, []);

  const setAccentColor = useCallback((color) => {
    _setAccentColor(color);
    localStorage.setItem("ACCENT_COLOR", color);
  }, []);

  const setDarkMode = useCallback((isDark) => {
    _setDarkMode(isDark);
    localStorage.setItem("DARK_MODE", isDark);
  }, []);

  const setToken = useCallback((token, expireTime) => {
    _setToken(token);
    if (token) {
      localStorage.setItem('ACCESS_TOKEN', token);
      localStorage.setItem('TOKEN_EXPIRATION', new Date(Date.now() + expireTime * 1000).toString());
    } else {
      localStorage.removeItem('ACCESS_TOKEN');
      localStorage.removeItem('TOKEN_EXPIRATION');
    }
  }, []);

  return (
    <StateContext.Provider value={{
      user,
      token,
      roles,
      permissions,
      unseenReleases,
      message,
      setUser,
      setToken,
      setRoles,
      setPermissions,
      setMessage,
      setUnseenReleases,
      darkMode,
      setDarkMode,
      accentColor,
      setAccentColor,
      company,
      setCompany,
    }}>
      {children}
    </StateContext.Provider>
  )
}
export const useStateContext = () => useContext(StateContext);
